import { useCallback, useEffect, useRef, useState } from "react";

import { CryptoSwapsService } from "../services/cryptoSwaps.service";
import type {
  OtcAmountAnchor,
  OtcVolumeDiscount,
  OtcVolumeDiscountPayload,
  OtcVolumeSuggestion,
  OtcVolumeSuggestionKind,
} from "../types/cryptoSwaps.types";
import { canonicalAmount } from "../utils/cryptoSwapAmounts";
import { typedAmount, volumeDiscountView } from "../utils/volumeDiscount";

type VolumeDiscountLoader = (
  quoteId: string,
  payload: OtcVolumeDiscountPayload,
  signal?: AbortSignal,
) => Promise<OtcVolumeDiscount | null>;

interface UseOtcVolumeDiscountOptions {
  quote: { id: string } | null;
  isPricing: boolean;
  anchor: OtcAmountAnchor;
  amount: string;
  isStaff: boolean;
  organizationId: string | null;
  load?: VolumeDiscountLoader;
}

interface ClickedSuggestion {
  insightId: string;
  kind: OtcVolumeSuggestionKind;
}

export function useOtcVolumeDiscount({
  quote,
  isPricing,
  anchor,
  amount,
  isStaff,
  organizationId,
  load = CryptoSwapsService.volumeDiscount,
}: UseOtcVolumeDiscountOptions) {
  const [insight, setInsight] = useState<OtcVolumeDiscount | null>(null);
  const [scope, setScope] = useState(isStaff ? organizationId : null);
  const clicked = useRef<ClickedSuggestion | null>(null);
  const quoteId = quote?.id ?? null;
  const currentScope = isStaff ? organizationId : null;

  if (scope !== currentScope) {
    setScope(currentScope);
    setInsight(null);
  }

  useEffect(() => {
    if (!quoteId || (isStaff && !organizationId)) return;
    const source = clicked.current;
    clicked.current = null;
    const controller = new AbortController();
    let settled = false;
    load(
      quoteId,
      {
        ...(isStaff && organizationId ? { organization_id: organizationId } : {}),
        ...(source ? { source_insight_id: source.insightId, source_kind: source.kind } : {}),
      },
      controller.signal,
    )
      .then((result) => {
        settled = true;
        setInsight(result);
      })
      .catch(() => {
        settled = true;
        if (!controller.signal.aborted) setInsight(null);
      });
    return () => {
      controller.abort();
      // StrictMode re-runs the effect for the same quote: the click must still reach that request.
      if (!settled && source && !clicked.current) clicked.current = source;
    };
  }, [quoteId, isStaff, organizationId, load]);

  const applySuggestion = useCallback(
    (suggestion: OtcVolumeSuggestion): string => {
      const next = typedAmount(suggestion.send_amount);
      const reprices = anchor !== "send" || canonicalAmount(amount) !== canonicalAmount(next);
      clicked.current = insight && reprices ? { insightId: insight.id, kind: suggestion.kind } : null;
      return next;
    },
    [insight, anchor, amount],
  );

  return { view: volumeDiscountView(insight, quoteId, isPricing), applySuggestion };
}
